import React, { useState, useRef, useEffect } from 'react';
import './MusicPlayer.css';
import MusicGuide from '../MusicGuide/MusicGuide';
import { 
  renderTrackIcon, 
  PlayIcon, 
  PauseIcon, 
  PreviousIcon, 
  NextIcon, 
  VolumeIcon, 
  HelpIcon 
} from './Icons';

const MusicPlayer = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(0);
  const [volume, setVolume] = useState(0.5);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const audioRef = useRef(null);

  // Danh sách nhạc nền thư giãn
  const musicTracks = [
    {
      id: 1,
      title: "Tiếng Mưa Nhẹ",
      artist: "Âm thanh thiên nhiên",
      url: "/audio/rain-01.mp3", // File local trong public/audio
      cover: "rain"
    },
    {
      id: 2,
      title: "Tiếng Sóng Biển",
      artist: "Âm thanh thiên nhiên",
      url: "/audio/ocean-01.mp3", // File local trong public/audio
      cover: "ocean"
    },
    {
      id: 3,
      title: "Tiếng Chim Hót",
      artist: "Âm thanh thiên nhiên",
      url: "/audio/birds-01.mp3", // File local trong public/audio
      cover: "birds"
    },
    {
      id: 4,
      title: "Nhạc Piano Nhẹ",
      artist: "Nhạc thư giãn",
      url: "/audio/piano-01.mp3", // File local trong public/audio
      cover: "piano"
    },
    {
      id: 5,
      title: "Nhạc Thư Giãn",
      artist: "Nhạc nền",
      url: "https://www.soundjay.com/misc/sounds/relaxing-music-01.mp3", // URL dự phòng
      cover: "music"
    }
  ];

  // Kiểm tra xem đã hiển thị hướng dẫn chưa
  useEffect(() => {
    const hasSeenGuide = localStorage.getItem('musicGuideSeen');
    if (!hasSeenGuide) {
      setShowGuide(true);
    }
  }, []);

  // Khởi tạo audio
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  // Xử lý thời gian phát nhạc
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      const updateTime = () => setCurrentTime(audio.currentTime);
      const updateDuration = () => setDuration(audio.duration);
      
      audio.addEventListener('timeupdate', updateTime);
      audio.addEventListener('loadedmetadata', updateDuration);
      
      return () => {
        audio.removeEventListener('timeupdate', updateTime);
        audio.removeEventListener('loadedmetadata', updateDuration);
      };
    }
  }, [currentTrack]);

  // Chuyển đổi phát/dừng
  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (audio) {
      if (isPlaying) {
        audio.pause();
      } else {
        audio.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Chuyển bài tiếp theo
  const nextTrack = () => {
    const next = (currentTrack + 1) % musicTracks.length;
    setCurrentTrack(next);
    setIsPlaying(false);
  };

  // Chuyển bài trước
  const previousTrack = () => {
    const prev = currentTrack === 0 ? musicTracks.length - 1 : currentTrack - 1;
    setCurrentTrack(prev);
    setIsPlaying(false);
  };

  // Thay đổi âm lượng
  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  // Chuyển đổi thời gian phát
  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (audio) {
      const newTime = parseFloat(e.target.value);
      audio.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  // Format thời gian
  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Xử lý đóng hướng dẫn
  const handleCloseGuide = () => {
    setShowGuide(false);
    localStorage.setItem('musicGuideSeen', 'true');
  };

  // Xử lý mở hướng dẫn
  const handleShowGuide = () => {
    setShowGuide(true);
  };

  return (
    <div className={`music-player ${isExpanded ? 'expanded' : ''}`}>
      {/* Audio element */}
      <audio
        ref={audioRef}
        src={musicTracks[currentTrack].url}
        onEnded={nextTrack}
        preload="metadata"
      />
      
      {/* Giao diện mở rộng */}
      {isExpanded && (
        <div className="music-player-content">
          {/* Thông tin bài hát */}
          <div className="track-info">
            <div className="track-cover">
              {renderTrackIcon(musicTracks[currentTrack].cover, 40)}
            </div>
            <div className="track-details">
              <div className="track-title">{musicTracks[currentTrack].title}</div>
              <div className="track-artist">{musicTracks[currentTrack].artist}</div>
            </div>
          </div>

          {/* Thanh tiến trình */}
          <div className="progress-container">
            <span className="time current-time">{formatTime(currentTime)}</span>
            <input
              type="range"
              min="0"
              max={duration || 0}
              value={currentTime}
              onChange={handleSeek}
              className="progress-bar"
            />
            <span className="time total-time">{formatTime(duration)}</span>
          </div>

          {/* Điều khiển */}
          <div className="music-controls">
            <button 
              className="control-btn prev-btn"
              onClick={previousTrack}
              title="Bài trước"
            >
              <PreviousIcon size={18} />
            </button>
            
            <button 
              className={`control-btn play-pause-btn ${isPlaying ? 'playing' : ''}`}
              onClick={togglePlayPause}
              title={isPlaying ? "Tạm dừng" : "Phát"}
            >
              {isPlaying ? <PauseIcon size={20} /> : <PlayIcon size={20} />}
            </button>
            
            <button 
              className="control-btn next-btn"
              onClick={nextTrack}
              title="Bài tiếp theo"
            >
              <NextIcon size={18} />
            </button>
          </div>

          {/* Điều chỉnh âm lượng */}
          <div className="volume-control">
            <span className="volume-icon">
              <VolumeIcon size={16} />
            </span>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={volume}
              onChange={handleVolumeChange}
              className="volume-slider"
            />
            <span className="volume-value">{Math.round(volume * 100)}%</span>
          </div>

          {/* Danh sách bài hát */}
          <div className="playlist">
            <div className="playlist-header">
              <h4>Danh sách nhạc</h4>
              <button 
                className="guide-btn"
                onClick={handleShowGuide}
                title="Hướng dẫn sử dụng"
              >
                <HelpIcon size={14} />
              </button>
            </div>
            <div className="track-list">
              {musicTracks.map((track, index) => (
                <div 
                  key={track.id}
                  className={`track-item ${index === currentTrack ? 'active' : ''}`}
                  onClick={() => {
                    setCurrentTrack(index);
                    setIsPlaying(false);
                  }}
                >
                  <span className="track-number">{index + 1}</span>
                  <span className="track-cover-small">
                    {renderTrackIcon(track.cover, 20)}
                  </span>
                  <div className="track-info-small">
                    <div className="track-title-small">{track.title}</div>
                    <div className="track-artist-small">{track.artist}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Music Guide */}
      {showGuide && <MusicGuide onClose={handleCloseGuide} />}
    </div>
  );
};

export default MusicPlayer;
